import { InjectProperty } from '@jack-henry/frontend-utils/di';
import { DynamicComponentExitReason } from '@jack-henry/frontend-utils/lit/dynamic-component-registry';
import { Account, DashboardService } from '@treasury/domain/dashboard';
import { NotificationService, TmBaseComponent } from '@treasury/presentation';
import { informationIcon } from '@treasury/presentation/assets/icons';
import '@treasury/presentation/components/tm-bottom-sheet';
import '@treasury/presentation/components/tm-button';
import '@treasury/presentation/components/tm-footer';
import { TmDialogButtonPosture, TmDialogService } from '@treasury/presentation/services/dialog';
import { html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import './add-account-group-menu';
import './available-accounts-sheet';
import './configure-group-accounts-sheet';
import './order-account-groups-table';

export type AccountGroup = { name: string; isRemoved: boolean; id: number; accounts: Account[] };

export const tagName = 'configure-account-groups-sheet';

@customElement(tagName)
export class ConfigureAccountGroupsSheet extends TmBaseComponent {
    @property({ type: Boolean })
    public open = false;

    @property({ type: Array })
    public accountGroups!: AccountGroup[];

    @InjectProperty()
    declare private dashboardService: DashboardService;

    @InjectProperty()
    declare private readonly dialogService: TmDialogService;

    @InjectProperty()
    declare private notificationService: NotificationService;

    @state()
    private loading = true;

    @state()
    private showAvailableAccounts = false;

    @state()
    private showOrderGroupAccounts = false;

    @state()
    private selectedGroup?: AccountGroup;

    public disconnectedCallback(): void {
        super.disconnectedCallback();
        this.dialogService.close();
    }

    protected async firstUpdated() {
        this.fetchAccountGroups();
    }

    protected async addAccountsToGroup() {
        if (!this.selectedGroup) {
            return;
        }
        this.loading = true;
        try {
            await this.dashboardService.addAccountsToGroup(
                this.selectedGroup.id || 0,
                this.selectedGroup.accounts || []
            );
            this.notificationService.renderSuccess(`Accounts added to ${this.selectedGroup.name}`);
            this.fetchAccountGroups();
        } catch (e: unknown) {
            if (e instanceof Error) {
                this.notificationService.renderError(e.message);
            } else {
                this.notificationService.renderError('An unknown error occurred');
            }
        } finally {
            this.loading = false;
        }
    }

    protected render() {
        return html`<tm-bottom-sheet
            horizontal
            .open=${this.open}
            @close=${() => {
                this.open = false;
                this.dispatchEvent(new CustomEvent('close'));
            }}
        >
            <div slot="header-center" class="flex flex-col">
                <span>Account Groups</span><span class="text-xs">Drag & drop to reorder</span>
            </div>
            <div slot="header-right">
                ${this.accountGroups?.length < 20
                    ? html`<add-account-group-menu
                          @createGroup=${(e: CustomEvent) => {
                              const groupName = e.detail;
                              if (!groupName) {
                                  return;
                              }
                              this.selectedGroup = {
                                  name: groupName,
                                  isRemoved: false,
                                  id: 0,
                                  accounts: [],
                              };
                              this.dispatchEvent(
                                  new CustomEvent('showAccountSelection', { detail: groupName })
                              );
                              this.showAvailableAccounts = true;
                          }}
                      ></add-account-group-menu>`
                    : nothing}
            </div>
            <div id="order-account-groups-table" class="mt-5 px-2">
                ${this.loading
                    ? html`<tm-loader card></tm-loader>`
                    : html`<order-account-groups-table
                          .accountGroups=${this.accountGroups}
                          @groups-reordered=${(e: CustomEvent) => {
                              this.accountGroups = e.detail.groups;
                              this.updateGroupIndex(e.detail.updatedGroup, e.detail.newIndex);
                          }}
                          @edit-group=${(e: CustomEvent) => {
                              const { detail } = e;
                              this.selectedGroup = detail;
                              this.showOrderGroupAccounts = true;
                          }}
                          @group-removed=${(e: CustomEvent) => {
                              const { group } = e.detail;
                              this.promptForAccountGroupDeletion(group);
                          }}
                      ></order-account-groups-table>`}
            </div>
            <tm-footer
                ><div
                    id="account-groups-additional-information"
                    class="flex px-4 py-4 gap-2 border-b border-gray-200"
                >
                    <tm-icon .icon=${informationIcon} color="#646769"></tm-icon>
                    <div class="text-secondary text-sm">
                        20 groups may be created with up to 50 accounts in each group
                    </div>
                </div></tm-footer
            >
            <configure-group-accounts-sheet
                .open=${this.showOrderGroupAccounts}
                .accountGroup=${this.selectedGroup}
                @addAccounts=${() => {
                    this.showAvailableAccounts = true;
                    this.showOrderGroupAccounts = false;
                    this.fetchAccountGroups();
                }}
                @groupNameUpdated=${() => {
                    this.fetchAccountGroups();
                }}
                @groupAccountOrderUpdated=${() => {
                    this.fetchAccountGroups();
                }}
                @accountRemoved=${() => {
                    this.fetchAccountGroups();
                }}
                @close=${() => {
                    this.showOrderGroupAccounts = false;
                }}
            ></configure-group-accounts-sheet>
            <available-accounts-sheet
                .open=${this.showAvailableAccounts}
                .accountGroup=${this.selectedGroup}
                @addAccountsToGroup=${(e: CustomEvent) => {
                    this.selectedGroup = e.detail;
                    this.showAvailableAccounts = false;
                    if (this.selectedGroup?.id) {
                        this.addAccountsToGroup();
                    } else {
                        if (!this.selectedGroup) {
                            return;
                        }
                        this.createAccountGroup(this.selectedGroup);
                    }
                    this.fetchAccountGroups();
                }}
                @groupNameUpdated=${() => {
                    this.fetchAccountGroups();
                }}
                @close=${() => {
                    this.showAvailableAccounts = false;
                }}
            ></available-accounts-sheet>
        </tm-bottom-sheet>`;
    }

    private async fetchAccountGroups() {
        this.loading = true;
        const groups = await this.dashboardService.getMyAccounts();
        this.accountGroups = groups.map(group => ({
            name: group.name,
            id: group.id,
            isRemoved: false,
            accounts: group.accounts,
        })) as AccountGroup[];
        this.loading = false;
    }

    private async updateGroupIndex(group: AccountGroup, index: number) {
        try {
            await this.dashboardService.updateAccountGroupOrder(group.id, index);
            this.notificationService.renderSuccess('Account group order updated');
        } catch {
            this.notificationService.renderError('Failed to update account group order');
        }
    }

    private async promptForAccountGroupDeletion(group: AccountGroup) {
        const { reason } = await this.dialogService.open(
            `Are you sure you want to delete the account group "${group.name}"?`,
            'Delete Account Group',
            {
                buttons: {
                    [TmDialogButtonPosture.Secondary]: {
                        label: 'Cancel',
                        reason: DynamicComponentExitReason.Cancel,
                    },
                    [TmDialogButtonPosture.Primary]: {
                        label: 'Delete',
                        reason: DynamicComponentExitReason.Confirm,
                    },
                },
                classes: ['max-w-[500px]'],
            }
        );
        if (reason === DynamicComponentExitReason.Confirm) {
            this.deleteAccountGroup(group);
        }
        if (reason === DynamicComponentExitReason.Cancel) {
            this.fetchAccountGroups();
        }
    }

    private async deleteAccountGroup(group: AccountGroup) {
        this.loading = true;
        try {
            await this.dashboardService.deleteAccountGroup(group.id);
            this.notificationService.renderSuccess('Account group deleted successfully');
            this.fetchAccountGroups();
        } catch (e: unknown) {
            if (e instanceof Error) {
                this.notificationService.renderError(e.message);
            } else {
                this.notificationService.renderError('An unknown error occurred');
            }
        }
        this.loading = false;
    }

    private async createAccountGroup(group: AccountGroup) {
        this.loading = true;
        try {
            await this.dashboardService.createAccountGroup(group);
            this.notificationService.renderSuccess('Account group created');
            this.fetchAccountGroups();
        } catch (e: unknown) {
            if (e instanceof Error) {
                this.notificationService.renderError(e.message);
            } else {
                this.notificationService.renderError('An unknown error occurred');
            }
        } finally {
            this.loading = false;
        }
    }
}

declare global {
    interface HTMLElementTagNameMap {
        [tagName]: ConfigureAccountGroupsSheet;
    }
}
